import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/feedback/Toaster';
import { submitFoutProblemen, submitFoutSleutel, submitWaarschuwingen, type SubmitProbleem } from '../submit-foutmelding';
import { ColumnList } from '../components/ColumnList';
import { SamplePanel } from '../components/SamplePanel';
import { ValidationPanel, isBlokkerend } from '../components/ValidationPanel';
import { SubmitBar } from '../components/SubmitBar';
import { ConfirmSubmitDialog } from '../components/ConfirmSubmitDialog';
import { DegradationBanner } from '../components/DegradationBanner';
import { SubmitErrorBanner } from '../components/SubmitErrorBanner';
import { ProfileAppliedBanner } from '../components/ProfileAppliedBanner';
import { ProductChangeList } from '../components/ProductChangeList';
import { RetailerChoiceBlock } from '../components/RetailerChoiceBlock';
import {
  useGs1FieldOptions,
  useIntakeSessionChanges,
  useSubmitIntakeSession,
  useUpdateIntakeColumn,
  useUpdateProductDecision,
  useUpdateRetailerChoice,
  useValidateIntakeSession,
} from '../hooks/useIntake';
import { isIntakeColumnReviewed, marketsWithoutRetailer } from '../review.utils';
import type { IntakeSession } from '../intake.api';

export interface IntakeReviewPageProps {
  session: IntakeSession;
  /**
   * h7-4: wat bij een geslaagde indiening wél is opgeslagen maar niet gepubliceerd.
   *
   * Gaat naar boven omdat dit scherm verdwijnt zodra de sessie op `submitted` staat; de melding
   * hoort juist op het scherm dát daarna in beeld komt.
   */
  onPublishWarnings?: (warnings: SubmitProbleem[]) => void;
}

// Technical action identifiers (the backend's PATCH contract), not user-facing copy — kept as
// named constants so no raw string literal appears at any JSX call site.
const COLUMN_ACTIONS = {
  setField: 'setField',
  confirm: 'confirm',
  ignore: 'ignore',
} as const;

/**
 * h5-5: the review screen — the heart of the intake flow (FR31-33). Composes ColumnList (left),
 * SamplePanel + ValidationPanel (right), and SubmitBar + ConfirmSubmitDialog. Every column change
 * PATCHes the session directly (no local save button, no local storage) so a resumed session
 * picks up exactly where the customer left off.
 */
export function IntakeReviewPage({ session, onPublishWarnings }: IntakeReviewPageProps) {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const columns = useMemo(() => session.mappingProposal ?? [], [session.mappingProposal]);
  const [activeIndex, setActiveIndex] = useState<number | null>(columns.length ? 0 : null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  // H13-9: de meldingen per regel van de laatste mislukte poging. Ze horen bij het scherm en niet
  // bij de sessie — na herladen zijn ze weg, en dan noemt de melding ze ook niet meer.
  const [submitProblemen, setSubmitProblemen] = useState<SubmitProbleem[]>([]);

  const { data: gs1Options } = useGs1FieldOptions(session.flow);
  const updateColumn = useUpdateIntakeColumn(session.id);
  const { data: validationErrors } = useValidateIntakeSession(session.id, true);
  const submitSession = useSubmitIntakeSession(session.id);
  const { data: changeDetection, isLoading: changesLoading } = useIntakeSessionChanges(session.id, session.status === 'review');
  const updateProductDecision = useUpdateProductDecision(session.id);
  const updateRetailerChoice = useUpdateRetailerChoice(session.id);

  const activeColumn = useMemo(() => (activeIndex === null ? null : (columns[activeIndex] ?? null)), [columns, activeIndex]);
  const unreviewedCount = columns.filter((column) => !isIntakeColumnReviewed(column)).length;
  const changedCount = columns.filter((column) => column.confirmed || column.ignored).length;
  const totalProductCount = changeDetection?.products.length ?? 0;
  const productChangeCount = changeDetection?.products.filter((product) => product.isChange).length ?? 0;
  // h13-5: elke doelmarkt in het bestand heeft minstens één afnemer nodig. De serverkant weigert
  // hetzelfde; deze telling stuurt alleen de knop.
  const openMarkets = marketsWithoutRetailer(session.targetMarkets, session.retailerChoice);

  // Op positie, niet op kop: in het verpakkingssjabloon heet elke materiaalgroep zijn kolommen
  // hetzelfde, en dan raakt een verzoek op kopnaam altijd de eerste.
  function patchColumn(index: number, action: 'setField' | 'confirm' | 'ignore', suggestedField?: string | null) {
    const kolom = columns[index];
    if (!kolom) return;
    updateColumn.mutate({ header: kolom.header, columnIndex: index, expectedVersion: session.version, action, suggestedField });
  }

  function handleConfirmSubmit() {
    submitSession.mutate(session.version, {
      onSuccess: (ingediend) => {
        setConfirmDialogOpen(false);
        // h7-4: artikelen die wél zijn opgeslagen maar niet gepubliceerd. Ze staan nergens
        // opgeslagen, dus ze moeten nú naar boven — dit scherm verdwijnt zodra de sessie op
        // `submitted` staat. Opnieuw indienen zou de gegevens dubbel opslaan.
        onPublishWarnings?.(submitWaarschuwingen(ingediend?.submitWarnings));
        // FR40: het ordernummer hoort in de melding zelf. Daarmee kan de klant zijn aanmelding
        // terugvinden en er met ons over praten; "gelukt" alleen laat hem met lege handen staan.
        // Voorzichtig lezen: dit is het geslaagde pad, en een melding mag daar nooit de oorzaak van
        // een crash zijn. Zonder ordernummer is de indiening wel gelukt maar niet terug te vinden;
        // dan is de gewone melding beter dan geen melding.
        // spec-aanmeldketen-sneller CAP-3: de melding volgt de status in het antwoord. `review`
        // betekent dat de keten al strandde; dan toont het scherm de bestaande foutbanner.
        if (ingediend?.status === 'review') return;
        if (ingediend?.status === 'submitting' && ingediend.orderNumber) {
          addToast({ key: 'intake.review.submitBar.receivedWithOrder', values: { orderNumber: ingediend.orderNumber } }, 'success');
          return;
        }
        addToast(
          ingediend?.orderNumber
            ? { key: 'intake.review.submitBar.successWithOrder', values: { orderNumber: ingediend.orderNumber } }
            : { key: 'intake.review.submitBar.success' },
          'success',
        );
      },
      onError: (fout) => {
        // Het bevestigingsvenster gaat ook bij een fout dicht. Bleef het staan, dan keek de klant
        // naar de vraag "deze aanmelding indienen?" terwijl de melding dát het mislukt is erachter
        // stond — gezien bij een beproeving op acceptatie 12-09-2026.
        setConfirmDialogOpen(false);
        // H13-9: bij een afgekeurd bestand staat in het antwoord wélke regels de klant moet
        // herstellen. Zonder dit verwees de melding naar meldingen die nergens stonden.
        setSubmitProblemen(submitFoutProblemen(fout));
        // Elke ketenfout draagt een eigen code (FR48); een tekst van CustomerPortal komt hier nooit.
        // Kennen we de code niet, dan is de algemene melding beter dan een rauwe sleutel.
        addToast({ key: submitFoutSleutel(fout) }, 'error');
      },
    });
  }

  return (
    <section data-testid="intake-review-page" className="grid gap-4 md:grid-cols-[minmax(0,1fr)_320px]">
      <div className="flex flex-col gap-3 md:hidden">
        <p role="status" className="rounded-[var(--radius-card)] bg-[var(--background-muted)] p-3 text-sm">
          {t('intake.review.smallScreenNotice')}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <h1 className="text-xl font-bold text-[var(--foreground-heading)]">{t('intake.review.title')}</h1>
        {session.degradationReason ? <DegradationBanner reason={session.degradationReason} /> : null}
        {/* h6-3: een vorige indienpoging die strandde. Zonder dit komt de klant terug op een schoon
            scherm en weet hij niet dat er al een concept bij CustomerPortal staat. */}
        <SubmitErrorBanner errorCode={session.submitErrorCode} orderNumber={session.orderNumber} problems={submitProblemen} />
        <ProfileAppliedBanner columns={columns} />
        <ColumnList
          columns={columns}
          activeIndex={activeIndex}
          gs1Options={gs1Options ?? []}
          disabled={updateColumn.isPending}
          onSelect={setActiveIndex}
          onSetField={(index, field) => patchColumn(index, COLUMN_ACTIONS.setField, field)}
          onConfirm={(index) => patchColumn(index, COLUMN_ACTIONS.confirm)}
          onIgnore={(index) => patchColumn(index, COLUMN_ACTIONS.ignore)}
        />
        <ProductChangeList
          changes={changeDetection}
          isLoading={changesLoading}
          decisions={session.productDecisions}
          disabled={updateProductDecision.isPending}
          onDecide={(gtin, decision) => updateProductDecision.mutate({ gtin, decision, expectedVersion: session.version })}
        />
        <ValidationPanel errors={validationErrors ?? []} />
        {/* h13-5: onderaan en niet bovenaan — de kolomcontrole is het werk, de afnemerskeuze is de
            afronding. Bovenaan zou hij het echte werk verdringen. */}
        <RetailerChoiceBlock
          markets={session.targetMarkets}
          choice={session.retailerChoice}
          disabled={updateRetailerChoice.isPending}
          onChoose={(entries) => updateRetailerChoice.mutate({ entries, expectedVersion: session.version })}
        />
        <SubmitBar
          unreviewedCount={unreviewedCount}
          hasValidationErrors={(validationErrors ?? []).some(isBlokkerend)}
          marketsWithoutRetailer={openMarkets}
          marketCount={session.targetMarkets.length}
          disabled={submitSession.isPending}
          onSubmit={() => setConfirmDialogOpen(true)}
        />
      </div>

      <div>
        <SamplePanel column={activeColumn} />
      </div>

      <ConfirmSubmitDialog
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        columnCount={columns.length}
        changedCount={changedCount}
        totalProductCount={totalProductCount}
        productChangeCount={productChangeCount}
        retailerChoice={session.retailerChoice}
        targetMarkets={session.targetMarkets}
        isSubmitting={submitSession.isPending}
        onConfirm={handleConfirmSubmit}
      />
    </section>
  );
}
